import { isEnabledLanguage, LanguageCode } from '../i18n/languages';
import { DEMO_GOAL_MAX_LENGTH } from './demoService';

/**
 * What a link into the flow may carry (T-165): riselio.com's goal box opens
 * #/start?goal=…&lang=…&src=promo_hero. The goal is only filled in, never sent: the visitor
 * confirms it. Anything malformed is dropped.
 */
export interface StartParams {
  /** The goal as typed on the site, on one line and at most DEMO_GOAL_MAX_LENGTH long. */
  goal?: string;
  /** The site's language. */
  lang?: LanguageCode;
  /** Where the link was (promo_hero, …), for the funnel. */
  src?: string;
}

/** The query of a hash route ('#/start?goal=x' -> goal=x), if any. */
const hashQuery = (hash: string): URLSearchParams | null => {
  const at = hash.indexOf('?');
  return at < 0 ? null : new URLSearchParams(hash.slice(at + 1));
};

export const readStartParams = (hash: string): StartParams => {
  const query = hashQuery(hash);
  if (!query) return {};
  const params: StartParams = {};
  const goal = (query.get('goal') || '').replace(/\s+/g, ' ').trim().slice(0, DEMO_GOAL_MAX_LENGTH).trim();
  if (goal) params.goal = goal;
  const lang = query.get('lang');
  if (isEnabledLanguage(lang)) params.lang = lang;
  const src = query.get('src') || '';
  if (/^[a-z0-9_]{1,40}$/.test(src)) params.src = src;
  return params;
};

// The link's source, kept for this page: the address loses its query as soon as the flow shows
// a step, but "Let's start" may be pressed later
let linkSource: string | null = null;

export const rememberSource = (src?: string) => {
  if (src) linkSource = src;
};

/** Where this visit came from, for onboarding_started: the link's src, else direct. */
export const startSource = (): string => linkSource ?? 'direct';

// A goal from a link starts the flow on the goal step, so "Let's start" (which counts the start)
// is never pressed: the flow counts it once instead
let linkedGoal = false;

export const markLinkedGoal = () => {
  linkedGoal = true;
};

/** True once after a link brought a goal. */
export const takeLinkedGoal = (): boolean => {
  const was = linkedGoal;
  linkedGoal = false;
  return was;
};

/** For tests. */
export const forgetLink = () => {
  linkSource = null;
  linkedGoal = false;
};
